import React, { useEffect, useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Table from '@cloudscape-design/components/table';
import FormField from '@cloudscape-design/components/form-field';
import Box from '@cloudscape-design/components/box';
import TextFilter from '@cloudscape-design/components/text-filter';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { ResponseViewer } from '../components/ResponseViewer';
import { TeamModal } from '../components/TeamModal';
import { Team } from '../components/teams';

const USER_TYPES = ['PERSON', 'TEAM', 'SYSTEM', 'EXTERNAL'] as const;

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<{ type: string; value: string }[]>([]);
  const [type, setType] = useState('PERSON');
  const [value, setValue] = useState('');
  const [filter, setFilter] = useState('');
  const [filterText, setFilterText] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [registering, setRegistering] = useState(false);
  const { loading, result, execute } = useApi();

  const loadTeams = () => {
    api('GET', '/team_users').then(d => { if (Array.isArray(d)) setTeams(d); }).catch(() => {});
  };

  const load = () => {
    const q = filter ? `?user_type=${encodeURIComponent(filter)}` : '';
    api('GET', `/users${q}`).then(d => { if (Array.isArray(d)) setUsers(d); }).catch(() => {});
  };
  useEffect(load, [filter]);
  useEffect(loadTeams, []);

  const create = () => execute(async () => {
    const r = await api('POST', '/user', { type, value });
    setValue('');
    load();
    return r;
  });

  const remove = (u: { type: string; value: string }) => execute(async () => {
    const r = await api('DELETE', '/user', u);
    load();
    return r;
  });

  const selectedTeam = type === 'TEAM' ? teams.find(t => t.team_id === value) : undefined;

  const filtered = users.filter(u =>
    !filterText || u.value.toLowerCase().includes(filterText.toLowerCase()) || u.type.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Users</Header>

      <Container header={<Header variant="h2">Create User</Header>}>
        <SpaceBetween direction="horizontal" size="s">
          <FormField label="Type">
            <Select
              selectedOption={{ value: type, label: type }}
              options={USER_TYPES.map(t => ({ value: t, label: t }))}
              onChange={({ detail }) => { setType(detail.selectedOption.value!); setValue(''); }}
            />
          </FormField>
          {type === 'TEAM' ? (
            <FormField
              label="Team"
              secondaryControl={
                <Button variant="inline-link" onClick={() => setRegistering(true)}>
                  Register a new team
                </Button>
              }
            >
              {/* Only registered teams can be picked: the service stores an unregistered name as-is, which never matches in Brass. */}
              <Select
                selectedOption={selectedTeam ? { value: selectedTeam.team_id, label: selectedTeam.team_name } : null}
                options={teams.map(t => ({ value: t.team_id, label: t.team_name, description: t.team_id }))}
                onChange={({ detail }) => setValue(detail.selectedOption.value!)}
                filteringType="auto"
                placeholder="Choose a registered team"
                empty="No teams registered"
              />
            </FormField>
          ) : (
            <FormField label="Value">
              <Input value={value} onChange={({ detail }) => setValue(detail.value)} placeholder="alias or system id" />
            </FormField>
          )}
          <Box padding={{ top: 'xxl' }}>
            <Button variant="primary" onClick={create} loading={loading} disabled={!value}>
              Create
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      <Table
        header={
          <Header
            counter={`(${filtered.length})`}
            actions={
              <Select
                selectedOption={filter ? { value: filter, label: filter } : { value: '', label: 'All types' }}
                options={[{ value: '', label: 'All types' }, ...USER_TYPES.map(t => ({ value: t, label: t }))]}
                onChange={({ detail }) => setFilter(detail.selectedOption.value!)}
              />
            }
          >
            All Users
          </Header>
        }
        filter={
          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            filteringPlaceholder="Find users"
          />
        }
        columnDefinitions={[
          {
            id: 'type',
            header: 'Type',
            cell: item => item.type,
            sortingField: 'type',
          },
          {
            id: 'value',
            header: 'Value',
            cell: item => item.value,
            sortingField: 'value',
          },
          {
            id: 'actions',
            header: 'Actions',
            width: 120,
            cell: item => (
              <Button variant="inline-link" onClick={() => remove(item)}>Delete</Button>
            ),
          },
        ]}
        items={filtered}
        sortingDisabled={false}
        empty={<Box textAlign="center" padding="l">No users found</Box>}
      />

      <TeamModal
        visible={registering}
        teams={teams}
        onCancel={() => setRegistering(false)}
        onSaved={(team, userCreated) => {
          setRegistering(false);
          loadTeams();
          load();
          // If the modal already created the TEAM user, leave the field empty rather than invite a duplicate.
          setValue(userCreated ? '' : team.team_id);
        }}
      />

            <ResponseViewer data={result?.data} error={result?.error} />
    </SpaceBetween>
  );
};
